<template>
  <v-container fluid class="pa-6 grey lighten-4 fill-height align-start">
    <v-row>
      <!-- LEFT PANEL: Invoice Header & Client Details -->
      <v-col cols="12" md="5" lg="4">
        <v-card outlined class="elevation-2 mb-4 pa-2">
          <v-toolbar flat color="primary" dark class="rounded-t">
            <v-toolbar-title class="headline font-weight-bold">
              Račun Fiskalizacija 1.0 - DEMO!!!
            </v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pt-6">
            <!-- Inline Date and Time Fields -->
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="document.documentDate"
                  label="Datum"
                  type="date"
                  outlined
                  class="accessible-field"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="document.documentTime"
                  label="Vrijeme"
                  type="time"
                  outlined
                  class="accessible-field"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <!-- v-btn-toggle only tints the label of the active button, which is too
                 weak to read at a glance. The selected side is filled blue, the other
                 stays white, so there is never a doubt which one is on. -->
            <v-btn-toggle
              v-model="customerType"
              mandatory
              class="mb-6 d-flex"
              height="56"
            >
              <v-btn
                value="private"
                :color="customerType === 'private' ? 'primary' : 'grey darken-3'"
                :class="customerType === 'private' ? 'white--text' : 'white'"
                :dark="customerType === 'private'"
                :depressed="customerType === 'private'"
                :outlined="customerType !== 'private'"
                class="flex-grow-1 text-none body-1 font-weight-bold"
              >
                <v-icon left>mdi-account</v-icon>
                Privatni kupac
              </v-btn>
              <v-btn
                value="company"
                :color="customerType === 'company' ? 'primary' : 'grey darken-3'"
                :class="customerType === 'company' ? 'white--text' : 'white'"
                :dark="customerType === 'company'"
                :depressed="customerType === 'company'"
                :outlined="customerType !== 'company'"
                class="flex-grow-1 text-none body-1 font-weight-bold"
              >
                <v-icon left>mdi-domain</v-icon>
                Firma
              </v-btn>
            </v-btn-toggle>

            <!-- One search over all partners: name, OIB or address -->
            <v-combobox
              ref="partnerSearch"
              v-model="selectedPartner"
              :items="allPartners"
              :filter="partnerFilter"
              item-text="partnerName"
              label="Traži kupca - naziv, OIB ili adresa"
              placeholder="npr. pana, 69688254638, zagreb"
              outlined
              clearable
              return-object
              hide-details
              prepend-inner-icon="mdi-account-search"
              class="accessible-field mb-4"
              @change="onPartnerSelect"
            >
              <template v-slot:item="{ item }">
                <v-list-item-avatar
                  size="34"
                  :color="item.privatePerson ? 'teal lighten-4' : 'blue-grey lighten-4'"
                >
                  <v-icon :color="item.privatePerson ? 'teal darken-2' : 'blue-grey darken-2'">
                    {{ item.privatePerson ? 'mdi-account' : 'mdi-domain' }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title
                    class="body-1 font-weight-medium"
                    :class="item.privatePerson ? 'teal--text text--darken-3' : ''"
                  >
                    {{ item.partnerName }}
                    <v-chip
                      x-small
                      dark
                      class="ml-2"
                      :color="item.privatePerson ? 'teal darken-1' : 'blue-grey'"
                    >
                      {{ item.privatePerson ? 'privatni kupac' : 'firma' }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    <span v-if="item.partnerVATID">OIB {{ item.partnerVATID }}</span>
                    <span v-else-if="item.privatePerson" class="teal--text">bez OIB-a</span>
                    <span v-if="partnerAddress(item)"> &middot; {{ partnerAddress(item) }}</span>
                    <span v-if="item.partneremail" class="ml-1">
                      <v-icon x-small>mdi-at</v-icon> {{ item.partneremail }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:no-data>
                <v-list-item class="py-2">
                  <v-list-item-content>
                    <v-list-item-title class="body-2">
                      Nema spremljenog kupca. Upišite podatke ispod.
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-combobox>

            <!-- Who is on the receipt - always visible, so it is never a guess -->
            <v-sheet
              outlined
              rounded
              class="pa-3 mb-4 d-flex align-center"
              :color="customerType === 'private' ? 'teal lighten-5' : 'blue-grey lighten-5'"
            >
              <v-icon
                size="30"
                :color="customerType === 'private' ? 'teal darken-2' : 'blue-grey darken-2'"
                class="mr-3"
              >
                {{ customerType === 'private' ? 'mdi-account' : 'mdi-domain' }}
              </v-icon>
              <div class="flex-grow-1">
                <div class="body-1 font-weight-bold">
                  {{ document.partnerName || 'POS - kupac bez podataka' }}
                </div>
                <div class="caption">
                  {{ customerType === 'private' ? 'Privatni kupac' : 'Firma' }}
                  <span v-if="document.partnerVATID"> &middot; OIB {{ document.partnerVATID }}</span>
                  <span v-else-if="customerType === 'company'" class="red--text"> &middot; OIB nedostaje</span>
                  <span v-if="invoice.email"> &middot; {{ invoice.email }}</span>
                </div>
              </div>
              <v-btn
                v-if="document.partnerName"
                small
                text
                color="grey darken-1"
                class="text-none"
                @click="clearCustomer"
              >
                <v-icon left small>mdi-close</v-icon>Očisti
              </v-btn>
            </v-sheet>

            

            <!-- Toggle: Retail vs Company -->
            

            <!-- Adaptive Customer Inputs -->
            <v-form ref="customerForm">
              <v-slide-y-transition mode="out-in">
                <!-- Case A: Private Person -->
                <div v-if="customerType === 'private'" key="private">

                  
                  <div v-if="showPrivateVat || document.partnerVATID">
                    <v-row class="align-center mt-n6" dense>
                      <v-col cols="8">
                        <v-text-field
                          v-model="invoice.vatId"
                          label="Osobni OIB"
                          outlined
                          class="accessible-field"
                          append-icon="mdi-close"
                          @click:append="showPrivateVat = false; invoice.vatId = ''"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row class="align-center mt-n7" dense>
                      <v-col cols="8">
                        <v-text-field
                          v-model="document.partnerStreet"
                          label="Ulica"
                          outlined
                          class="accessible-field "
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="4">
                        <v-text-field
                          v-model="document.partnerBuildingNameNumber"
                          label="Broj"
                          outlined
                          class="accessible-field "
                          hide-details
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row class="align-center" dense>
                      <v-col cols="8">
                        <v-text-field
                          v-model="document.partnerCity"
                          label="Grad"
                          outlined
                          class="accessible-field "
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="4">
                        <v-text-field
                          v-model="document.partnerPostcode"
                          label="Pošt.broj"
                          outlined
                          class="accessible-field "
                          hide-details
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </div>
                 
                  <div v-else class="text-right mt-n4 mb-4">
                    <v-btn text color="primary" @click="showPrivateVat = true" class="body-1 font-weight-bold">
                      + Dodaj podatke
                    </v-btn>
                  </div>
                </div>

                <!-- Case B: Company -->
                <div v-else key="company">

                  <v-row class="align-center">
                    <v-col cols="8">
                      <v-text-field
                        v-model="document.partnerVATID"
                        label="Firma OIB *"
                        outlined
                        class="accessible-field"
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-btn block color="secondary" height="56" outlined @click="verifyVatCompany" class="body-1">
                        PROVJERI
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-text-field
                    v-model="document.partnerName"
                    label="Naziv firme *"
                    outlined
                    class="accessible-field mt-4"
                    hide-details
                  ></v-text-field>

                   <v-row class="align-center">
                    <v-col cols="8">
                      <v-text-field
                        v-model="document.partnerStreet"
                        label="Ulica"
                        outlined
                        class="accessible-field mt-4"
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="document.partnerBuildingNameNumber"
                        label="Broj"
                        outlined
                        class="accessible-field mt-4"
                        hide-details
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="align-center" dense>
                    <v-col cols="8">
                      <v-text-field
                        v-model="document.partnerCity"
                        label="Grad"
                        outlined
                        class="accessible-field mt-4"
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="document.partnerPostcode"
                        label="Pošt.broj"
                        outlined
                        class="accessible-field mt-4"
                        hide-details
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  
                </div>
              </v-slide-y-transition>

              <v-checkbox
                v-model="invoice.saveToDb"
                label="Spremi/Update u bazu"
                color="primary"
                class="mt-4 v-input--accessible-checkbox"
                hide-details
              ></v-checkbox>

              <v-divider class="my-4"></v-divider>

              <!-- Global Tax Flag -->
              <v-switch 
                v-model="invoice.applyVat" 
                color="success" 
                hide-details 
                label="Primjeni PDV"
                class="v-input--accessible-switch"
              ></v-switch>

              <v-divider class="my-4"></v-divider>

              <!-- Collapsible Comment Field -->
              <v-expansion-panels flat tile class="grey lighten-5 border-all">
                <v-expansion-panel class="transparent">
                  <v-expansion-panel-header class="px-4 py-3 body-1 font-weight-medium text--secondary">
                    <span><v-icon left>mdi-comment-text-outline</v-icon> Dodaj napomenu</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-textarea v-model="invoice.comment" outlined rows="2" class="accessible-field" hide-details></v-textarea>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- RIGHT PANEL: Main Workspace (Search, Interactive Cart Matrix, Payments) -->
      <v-col cols="12" md="7" lg="8">
        <v-card outlined class="elevation-2 d-flex flex-column pa-2" style="min-height: 800px;">
          <v-toolbar flat color="grey lighten-3" class="rounded-t">
            <v-toolbar-title class="headline font-weight-bold grey--text text--darken-3">
              Stavke računa
            </v-toolbar-title>
          </v-toolbar>

          <v-card-text class="flex-grow-1 pa-4 d-flex flex-column">
            <!-- Search & Add Items (DB Lookup or Custom string generation) -->
            <v-combobox
              ref="itemSearch"
              v-model="itemSearchModel"
              :items="itemDatabase"
              item-text="itemName"
              :filter="itemFilter"
              label="Skeniraj kod ili upiši naziv ...   (F2)"
              outlined
              prepend-inner-icon="mdi-barcode-scan"
              clearable
              return-object
              class="accessible-field mb-4"
              @change="handleItemSelection"
              @keyup.enter="onSearchEnter"
            >
              <template v-slot:no-data>
                <v-list-item @click="addCustomItemDirectly" class="py-2">
                  <v-list-item-content>
                    <v-list-item-title class="body-1">
                      Ne mogu pronaći. Kliknite  <strong class="primary--text">"{{ itemSearchModel }}"</strong> da spremite artikal/uslugu.
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-combobox>

            <v-alert v-if="loadMismatch" type="warning" prominent class="mb-4">
              <div class="body-1 font-weight-medium mb-1">
                Učitani račun se ne slaže s izračunom iz stavaka.
              </div>
              <div class="body-2">
                U bazi: <strong>{{ localMoney(loadMismatch.stored) }} €</strong> &middot;
                izračunato iz stavaka: <strong>{{ localMoney(loadMismatch.calculated) }} €</strong>.
                Stavka ima popust upisan samo u iznosu, a ne i u cijeni, pa se cijena ne može
                točno rekonstruirati. Spremanje bi promijenilo iznos računa.
              </div>
              <v-checkbox
                v-model="allowMismatchSave"
                hide-details
                class="mt-2"
                label="Svejedno dopusti spremanje"
              ></v-checkbox>
            </v-alert>

            <!-- Interactive Spreadsheet Workspace Table (Enlarged Fonts and Paddings) -->
            <v-simple-table class="border-all flex-grow-1 accessible-table">
              <template v-slot:default>
                <thead>
                  <tr class="grey lighten-4">
                    <th class="text-left body-1 font-weight-bold" style="width: 30%;">Naziv</th>
                    <th class="text-center body-1 font-weight-bold" style="width: 10%;">Kol</th>
                    <th class="text-right body-1 font-weight-bold" style="width: 15%;">Neto cijena(€)</th>
                    <th class="text-center body-1 font-weight-bold" style="width: 15%;">PDV %</th>
                    <th class="text-right body-1 font-weight-bold" style="width: 12%;">Popust(€)</th>
                    <th class="text-right body-1 font-weight-bold" style="width: 15%;">Bruto (€)</th>
                    <th class="text-center" style="width: 3%;"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in cartItems" :key="index" class="table-row-large">
                    <!-- Item Name -->
                    <td>
                      <v-text-field v-model="item.name" hide-details tabindex="-1" class="pt-0 mt-0 plain-text-field body-1 font-weight-medium"></v-text-field>
                    </td>
                    <!-- Quantity -->
                    <td class="text-center">
                      <v-text-field v-model.number="item.qty" type="number" hide-details @keyup.enter="backToSearch" class="pt-0 mt-0 center-input body-1" style="width: 70px; margin: 0 auto;"></v-text-field>
                    </td>
                    <!-- Original Base Net Price -->
                    <td class="text-right">
                      <v-text-field v-model.number="item.basePrice" type="number" step="0.01" hide-details @keyup.enter="backToSearch" class="pt-0 mt-0 right-input body-1" style="width: 90px; margin-left: auto;" @input="recalculateFromBase(item)"></v-text-field>
                    </td>
                    <!-- Tax Rate Selection Tier Dropdown -->
                    <td class="text-center">
                      <v-select
                        v-model="item.taxRate"
                        :items="taxTiers"
                        item-text="label"
                        item-value="value"
                        hide-details
                        class="pt-0 mt-0 body-1 plain-text-field center-input font-weight-medium"
                        style="width: 110px; margin: 0 auto;"
                        @change="recalculateFromBase(item)"
                      ></v-select>
                    </td>
                    <!-- Flat Discount Input Field -->
                    <td class="text-right">
                      <v-text-field v-model.number="item.discount" type="number" step="0.01" hide-details @keyup.enter="backToSearch" class="pt-0 mt-0 right-input body-1 red--text font-weight-medium" style="width: 80px; margin-left: auto;" @input="recalculateFromDiscount(item)"></v-text-field>
                    </td>
                    <!-- Target Gross Input (Modifies values backwards to balance books natively) -->
                    <td class="text-right">
                      <v-text-field v-model.number="item.targetGross" type="number" step="0.01" hide-details @keyup.enter="backToSearch" class="pt-0 mt-0 right-input font-weight-bold generic-gross-field body-1" style="width: 100px; margin-left: auto;" @input="recalculateFromTargetGross(item)"></v-text-field>
                    </td>
                    <!-- Delete Item Row Button -->
                    <td class="text-center">
                      <v-btn icon color="error" tabindex="-1" @click="removeItem(index)"><v-icon>mdi-delete</v-icon></v-btn>
                    </td>
                  </tr>
                  <tr v-if="cartItems.length === 0">
                    <td colspan="7" class="text-center py-16 title grey--text text--darken-1">
                      Nema stavka na računu. Skenirajte kod ili upišite naziv...
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>

          <!-- BOTTOM SECTION: Multi-tax Itemized Recaps & Inline Card Payment Processor Matrix -->
          <v-card-text class="grey lighten-4 border-top pa-6">
            <v-row class="align-center">
              <!-- Tax Breakdown Block Engine summary profiles -->
              <v-col cols="12" md="5" class="d-flex flex-column justify-center py-0">
                <div class="d-flex justify-space-between mb-2 body-1 text--secondary">
                  <span>Neto ukupno:</span><span>{{ localMoney(totals.subtotal) }} €</span>
                </div>
                <!-- Mini Tax Ledger per tier layer -->
                <div v-for="(amount, rate) in totals.taxLedger" :key="rate" class="d-flex justify-space-between mb-2 body-1 grey--text text--darken-2">
                  <span>PDV stopa ({{ (rate * 100) }}%):</span><span>{{ localMoney(amount) }} €</span>
                </div>
                <v-divider class="my-3"></v-divider>
                <div class="d-flex justify-space-between align-center">
                  <span class="headline font-weight-bold">Ukupno:</span>
                  <span class="display-1 font-weight-black primary--text">{{ localMoney(totals.finalTotal) }} €</span>
                </div>
              </v-col>

              <!-- Payment Action Selection Grid Layout Matrix -->
              <v-col cols="12" md="7" class="border-left-desktop pl-md-6 py-0">
                <span class="title font-weight-bold text--secondary d-block mb-3">Vrsta plaćanja:</span>
                <v-item-group v-model="invoice.paymentMethod" mandatory>
                  <v-row dense>
                    <v-col v-for="method in paymentMethods" :key="method.value" cols="6" class="pa-2">
                      <v-item :value="method.value" v-slot:default>
                        <v-card
                          :color="invoice.paymentMethod === method.value ? 'primary' : 'white'"
                          :dark="invoice.paymentMethod === method.value"
                          outlined
                          class="text-center d-flex align-center justify-center cursor-pointer transition-swing elevation-1"
                          height="64"
                          @click="invoice.paymentMethod = method.value"
                        >
                          <span class="title font-weight-bold">
                            <v-icon left size="26" :dark="invoice.paymentMethod === method.value">{{ method.icon }}</v-icon>
                            {{ method.label }}
                          </span>
                        </v-card>
                      </v-item>
                    </v-col>
                  </v-row>
                </v-item-group>
              </v-col>
            </v-row>

            <v-slide-y-transition>
              <div v-if="invoice.paymentMethod === 'cash'">
                <v-divider class="my-4"></v-divider>
                <v-row class="align-center" dense>
                  <v-col cols="12" md="5">
                    <v-text-field
                      ref="tenderedField"
                      v-model.number="cashTendered"
                      type="number"
                      step="0.01"
                      min="0"
                      label="Primljeno gotovine   (F3)"
                      outlined
                      hide-details
                      prepend-inner-icon="mdi-cash"
                      class="accessible-field"
                    ></v-text-field>
                    <div class="mt-2">
                      <v-btn
                        v-for="q in cashQuickPicks"
                        :key="q.label"
                        small
                        outlined
                        class="mr-1 mb-1 text-none"
                        @click="cashTendered = q.value"
                      >{{ q.label }}</v-btn>
                      <v-btn small text class="mb-1 text-none" @click="cashTendered = null">Očisti</v-btn>
                    </div>
                  </v-col>
                  <v-col cols="12" md="7" class="text-md-right">
                    <div v-if="cashTendered !== null && cashTendered !== ''">
                      <div v-if="cashChange >= 0">
                        <span class="title font-weight-bold text--secondary">Za vratiti:</span>
                        <span class="display-1 font-weight-black success--text ml-3">{{ localMoney(cashChange) }} €</span>
                      </div>
                      <div v-else>
                        <span class="title font-weight-bold text--secondary">Nedostaje:</span>
                        <span class="display-1 font-weight-black error--text ml-3">{{ localMoney(-cashChange) }} €</span>
                      </div>
                    </div>
                    <span v-else class="body-1 grey--text">Upišite primljeni iznos za izračun povrata</span>
                  </v-col>
                </v-row>
              </div>
            </v-slide-y-transition>

            <v-divider class="my-4"></v-divider>

            <!-- Receipt Distribution Framework Toggle Layout -->
            <v-row class="align-center mb-4">
              <v-col cols="12" md="5">
                <v-btn-toggle v-model="deliveryMethod" mandatory class="d-flex" height="56">
                  <v-btn
                    value="print"
                    :color="deliveryMethod === 'print' ? 'primary' : 'grey darken-3'"
                :class="deliveryMethod === 'print' ? 'white--text' : 'white'"
                    :dark="deliveryMethod === 'print'"
                    :depressed="deliveryMethod === 'print'"
                    :outlined="deliveryMethod !== 'print'"
                    class="flex-grow-1 text-none body-1 font-weight-bold"
                  ><v-icon left>mdi-printer</v-icon>Isprintaj</v-btn>
                  <v-btn
                    value="email"
                    :color="deliveryMethod === 'email' ? 'primary' : 'grey darken-3'"
                :class="deliveryMethod === 'email' ? 'white--text' : 'white'"
                    :dark="deliveryMethod === 'email'"
                    :depressed="deliveryMethod === 'email'"
                    :outlined="deliveryMethod !== 'email'"
                    class="flex-grow-1 text-none body-1 font-weight-bold"
                  ><v-icon left>mdi-email-outline</v-icon>Šalji na email</v-btn>
                </v-btn-toggle>
              </v-col>
              <v-col cols="12" md="7">
                <v-slide-x-transition>
                  <v-text-field
                    v-if="deliveryMethod === 'email'"
                    v-model="invoice.email"
                    label="Email adresa kupca *"
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-at"
                    placeholder="kupac@primjer.com"
                    class="white accessible-field"
                  ></v-text-field>
                </v-slide-x-transition>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Receipt Distribution Framework Toggle Layout -->
            <v-row class="align-center mb-4">
              <v-col cols="12" md="7">
                  <v-btn @click="register" class="flex-grow-1 text-none body-1 font-weight-medium"><v-icon left>mdi-content-save-outline</v-icon>Spremi</v-btn>
                  <v-btn @click="printPonuda" class="flex-grow-1 text-none body-1 font-weight-medium"><v-icon left>mdi-printer</v-icon>Print kao ponuda</v-btn>
                  <v-btn @click="printRadniNalog" class="flex-grow-1 text-none body-1 font-weight-medium"><v-icon left>mdi-printer</v-icon>Print kao radni nalog</v-btn>
              </v-col>
              <v-col cols="12" md="5">

              </v-col>
            </v-row>

            <!-- Final Process Button execution hook -->
            <v-btn block color="success" height="70" class="headline font-weight-black mt-2 elevation-2" :disabled="cartItems.length === 0" @click="handleInvoiceSubmission">
              <v-icon left size="30">{{ deliveryMethod === 'email' ? 'mdi-email-send' : 'mdi-check-circle' }}</v-icon>
              {{ isCardPayment ? 'Šaljem na terminal' : (deliveryMethod === 'email' ? 'Završi i šalji email' : 'Završi i isprintaj') }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <DocumentPonudaNalogPrint ref="ponuda" />

    <v-snackbar v-model="snackbar" :color="color" :timeout="timeout" bottom>
      <span class="body-1 font-weight-medium">{{ text }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Zatvori</v-btn>
      </template>
    </v-snackbar>

    <!-- ACCESSIBLE MODAL SYSTEM: Async Blocking Overlay Dialog with Large Readable Layouts -->
    <v-dialog v-model="isProcessingTerminal" persistent max-width="550">
      <v-card color="indigo darken-4" dark class="text-center pa-8 tile">
        <v-card-text class="pt-4 white--text">
          <div class="display-1 font-weight-bold mb-4">POS Terminal Aktivan</div>
          <v-progress-circular indeterminate color="white" size="80" width="8" class="mb-6"></v-progress-circular>
          <div class="headline mb-2 font-weight-medium">Čekam autorizaciju...</div>
          <div class="display-2 yellow--text font-weight-black tracking-wide my-6">{{ localMoney(totals.finalTotal) }} €</div>
          <p class="title text--lighten-2 font-weight-light line-height-large">Molim uputite kupca da prisloni karticu i upiše pin na POS terminalu.</p>
        </v-card-text>
        <v-card-actions class="justify-center pb-2 mt-4">
          <v-btn color="error" x-large dark class="body-1 font-weight-bold px-6" outlined @click="abortTerminalPayment">Odustani & Vrati</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


  </v-container>
</template>

<script>

import langEn from './documentDescEn'
import langHr from './documentDescHR'
import commEn from '@/language/commonEN'
import commHr from '@/language/commonHR'
import dayjs from 'dayjs'
import TeyaService from '@/services/TeyaService'
import PartnerService from '@/services/PartnerService'
import BankAccountService from '@/services/BankAccountService'
import DocumentService from '@/services/DocumentService'
import DocumentDetailService from '@/services/DocumentDetailService'
import {detailsToCart, cartToDetails, documentToInvoice, invoiceToDocument, documentToPayment, documentToCustomerType, PAYMENT_TYPES} from './posMapper'
import {buildPrintPayload, sendToPrinter, printServiceUrl} from './posPrint'
import DocumentPonudaNalogPrint from './DocumentPonudaNalogPrint'
import { parseNum, round } from '@/snippets/allSnippets'
import moneyFormat from '@/snippets/moneyFormat'
import { defDocument } from './documentHelper'
import {defDocumentDetail, defNewDocumentDetail} from './documentHelper'
import POStestlist from './POStestlist'
import POStestlist2 from './POStestlist2'



export default {
  name: 'PosCompleteInvoiceEngine',
  data() {
    return {
      lang: {},
      langC: {},
      // the component already set snackbar/text/color in ten places, but neither the
      // data nor the snackbar itself existed - the messages were silently dropped
      snackbar: false,
      text: '',
      color: 'success',
      timeout: 4000,
      customerType: 'private',
      showPrivateVat: false,
      itemSearchModel: null,
      selectedPartner: null,
      selectedPrivateClient: null,
      selectedCompanyClient: null,
      deliveryMethod: 'print',
      
      isProcessingTerminal: false,
      terminalTimeoutId: null,

      componentKey: 0,
      document: {},
      documentDet: {},
      documentItems: [],
      mainQuery: {},
      banks: [],
      selectBank: null,
      selectBankId: null,
      selectId: null,

      cashTendered: null,
      // set on load when the stored total cannot be reproduced from the stored lines
      loadMismatch: null,
      allowMismatchSave: false,
      invoice: {
        date: new Date().toISOString().substr(0, 10),
        time: new Date().toTimeString().substr(0, 5),
        customerName: '',
        companyName: '',
        vatId: '',
        address: '',
        email: '',
        applyVat: true,
        comment: '',
        paymentMethod: 'cash',
        saveToDb: true
      },

      taxTiers: [
        { label: '25% PDV', value: 0.25 },
        { label: '13% PDV', value: 0.13 },
        { label: '5% PDV', value: 0.05 },
        { label: '0% Bez PDVa', value: 0.00 }
      ],

      // one entry per payment field on the document
      paymentMethods: PAYMENT_TYPES.map(p => ({label: p.label, value: p.value, icon: p.icon})),

      itemDatabase: [
        // { name: 'Wireless Mouse M123', price: 35.00, taxRate: 0.25 },
        // { name: 'USB-C Hub (6-in-1)', price: 89.00, taxRate: 0.25 },
        // { name: 'HDMI Cable (3m)', price: 12.50, taxRate: 0.13 },
        // { name: 'Fresh Espresso Beans (1kg)', price: 24.00, taxRate: 0.05 },
        // { name: 'International Shipping Duty', price: 15.00, taxRate: 0.00 }
      ],

      savedPrivateClients: [
        // { name: 'John Doe', vatId: 'HR987654321', email: 'john.doe@gmail.com' },
        // { name: 'Anna Smith', vatId: '', email: 'anna.s@outlook.com' }
      ],
      savedCompanies: [
        // { companyName: 'TechCorp Solutions Inc.', vatId: 'HR12345678901', address: '123 Business Ave, Zagreb', email: 'billing@techcorp.hr' },
        // { companyName: 'Global Trade d.o.o.', vatId: 'HR55544433322', address: 'Slavonska Avenija 4, Split', email: 'accounting@globaltrade.hr' }
      ],

      cartItems: []
    };
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onGlobalKey)
  },
  async mounted(){
    window.addEventListener('keydown', this.onGlobalKey)
    // the cashier starts by scanning, so the search field has the caret from the start
    this.$nextTick(() => { this.focusSearch() })
    if (this.$store.state.language === 'EN') {
      this.lang = langEn
      this.langC = commEn
    }
    if (this.$store.state.language === 'HR') {
      this.lang = langHr
      this.langC = commHr
    }
    if (this.$store.state.partnerStore) {
      this.savedPrivateClients = this.$store.state.partnerStore.filter(par => par.privatePerson === true)
      this.savedCompanies = this.$store.state.partnerStore.filter(par => par.privatePerson === false)
    }
    this.itemDatabase = this.$store.state.itemStore ? this.$store.state.itemStore : []
    await this.loadBankAccounts()
    console.log('this.$store.state.editItem ', this.$store.state.editItem)
    console.log('this.$store.state.documentActivHead ', this.$store.state.documentActivHead)
    if (this.$store.state.editItem === 1) {
        this.documentName = this.$store.state.documentActiv
        this.documentNameLoc = this.$store.state.documentActivLoc        
        if (this.$store.state.documentActivHead) {
          this.document = this.$store.state.documentActivHead
          // this.partners was never declared on this component, so this threw as soon
          // as a saved document actually had a partnerId. The list is allPartners, and
          // loadFromDocument() below is what puts the customer back on screen.
          if (this.document.partnerId && parseInt(this.document.partnerId) > 0) {
            this.selectId = parseInt(this.document.partnerId)
            const byId = this.allPartners.find(par => parseInt(par.id) === this.selectId)
            const byVat = !byId && this.document.partnerVATID
              ? this.allPartners.find(par => par.partnerVATID === this.document.partnerVATID)
              : null
            const found = byId || byVat
            if (found && found.id) {
              this.$store.dispatch('setPartnerid', found.id)
            }
          }
          // this.selectBankId  =this.document.bankAccountId ? parseInt(this.document.bankAccountId) : null
          // this.selectBank = this.selectBankId ? this.banks.find(bank => (bank.id === this.selectBankId)) : null
          this.document.documentTime = this.document.documentTime ? this.document.documentTime.substr(0,5) : null
          this.show1 = this.document.documentDescription4 && this.document.documentDescription4.trim().length > 0 ? true : false
          this.show2 = this.document.documentDescription1 && this.document.documentDescription1.trim().length > 0 ? true : false
          this.show3 = this.document.documentDescription5 && this.document.documentDescription5.trim().length > 0 ? true : false
          this.show4 = this.document.documentDescription3 && this.document.documentDescription3.trim().length > 0 ? true : false

          // Fill the screen from the saved invoice. Without this the header was
          // loaded but the cart stayed empty, so opening an old POS receipt showed
          // no lines at all.
          this.loadFromDocument()

        } else {
          // if in any case dont have all data in head, return to list
          this.text = 'Error! Sorry... some error happend. Need to return.'
          this.color = 'red'
          this.snackbar = true
          this.timeout = 4000
            this.$router.push({
              name: 'documentposindex'
            })
        }
     }   
    // create NEW
    if (this.$store.state.editItem === 0) {
      this.document = defDocument()
      if (this.$store.state.companyid) {
        this.CompanyId = this.$store.state.companyid
        this.mainQuery.CompanyId = this.$store.state.companyid
      }
      this.mainQuery.BusinessYear = this.$store.state.businessYear ? this.$store.state.businessYear : 2026
      this.mainQuery.documentName = this.$store.state.documentActiv ? this.$store.state.documentActiv : ''
      this.document.documentSide = this.$store.state.documentSide ? this.$store.state.documentSide : 0

      this.document.UserId = this.$store.state.user.id ? this.$store.state.user.id : ''
      this.document.email = this.$store.state.user.email ? this.$store.state.user.email : 'test@cegrupa.com'
      this.document.First = this.$store.state.user.First ? this.$store.state.user.First : 'NoFirst'
      this.document.Last = this.$store.state.user.Last ? this.$store.state.user.Last : 'NoLast'
      this.document.uservatID = this.$store.state.user.vatID ? this.$store.state.user.vatID : '12345678901'
      this.document.CompanyId = this.$store.state.companyid ? this.$store.state.companyid : 0
      this.mainQuery.CompanyId = this.$store.state.companyid ? this.$store.state.companyid : 0
      this.document.CompanyNameDatabase = this.$store.state.companyName ? this.$store.state.companyName : ''
      this.document.BusinessYear = this.$store.state.businessYear ? this.$store.state.businessYear : 2024
      this.mainQuery.BusinessYear = this.$store.state.businessYear ? this.$store.state.businessYear : 2024
      if (this.$store.state.documentActiv) {
        this.mainQuery.documentName = this.$store.state.documentActiv ? this.$store.state.documentActiv : ''
        this.document.documentName = this.$store.state.documentActiv ? this.$store.state.documentActiv : ''
        this.docName = this.$store.state.documentActiv
        this.document.documentAnalysis = 2
        this.$store.dispatch('setDocumentAnalysis', 2)
      }
      this.document.documentSide = this.$store.state.documentSide ? this.$store.state.documentSide : 1  
      // the default account is applied in loadBankAccounts(), after the list arrives
      const nrOfDays = 0
      this.document.documentDate = dayjs(new Date()).format('YYYY-MM-DD')
      this.document.documentTime = dayjs(new Date()).format('HH:mm')
      this.document.documentInvoiceStartDate = dayjs(new Date()).format('YYYY-MM-DD')
      this.document.documentInvoiceEndDate = dayjs(new Date()).add(nrOfDays, 'days').format('YYYY-MM-DD')
      this.document.documentInvoiceEndPeriodDate = dayjs(new Date()).format('YYYY-MM-DD')
      this.document.documentHomeCurrency = 'EUR'
      this.document.documentForeignCurrency = 'EUR'
      this.document.documentExRate = 1
    }

  },
  computed: {
    // What the cashier has to hand back. Negative means the customer still owes.
    cashChange() {
      const got = parseFloat(this.cashTendered)
      if (isNaN(got)) { return 0 }
      return Math.round((got - this.totals.finalTotal) * 100) / 100
    },

    // Exact amount plus the notes a customer usually hands over
    cashQuickPicks() {
      const total = this.totals.finalTotal
      const picks = [{label: 'Točno ' + this.localMoney(total), value: Math.round(total * 100) / 100}]
      ;[5, 10, 20, 50, 100, 200].forEach(n => {
        if (n >= total) { picks.push({label: String(n), value: n}) }
      })
      return picks.slice(0, 5)
    },

    // Both kinds live in the same table; privatePerson is the only difference and
    // private buyers have no OIB, so the search must not rely on it.
    allPartners() {
      return this.$store.state.partnerStore ? this.$store.state.partnerStore : []
    },

    isCardPayment() {
      return this.invoice.paymentMethod === 'card';
    },

    totals() {
      let subtotal = 0;
      let taxLedger = { 0.25: 0, 0.13: 0, 0.05: 0, 0.00: 0 };

      this.cartItems.forEach(item => {
        const netItemUnitPrice = item.basePrice - item.discount;
        const netRowTotal = netItemUnitPrice * item.qty;
        
        subtotal += netRowTotal;

        if (this.invoice.applyVat && item.taxRate > 0) {
          const rowCalculatedTax = netRowTotal * item.taxRate;
          taxLedger[item.taxRate] += rowCalculatedTax;
        }
      });

      const totalTax = Object.values(taxLedger).reduce((acc, curr) => acc + curr, 0);
      const finalTotal = subtotal + totalTax;

      return { subtotal, taxLedger, totalTax, finalTotal };
    }
  },

  watch: {
    'selectedPartner' () {
      if (!this.selectedPartner) {
        this.clearAllPartnerData()
      }
    },
    'selectedPrivateClient' () {
      if (!this.selectedPrivateClient) {
        this.clearAllPartnerData()
      }
    },
    'selectedCompanyClient' () {
      if (!this.selectedCompanyClient) {
        this.clearAllPartnerData()
      }
    },
    
    customerType() {
     // this.selectedPrivateClient = null;
     // this.selectedCompanyClient = null;
     this.clearAllPartnerData()
    }
  },

  methods: {
    clearAllPartnerData() {
      this.clearPartner()
      this.invoice = {
        date: new Date().toISOString().substr(0, 10),
        time: new Date().toTimeString().substr(0, 5),
        customerName: '',
        companyName: '',
        vatId: '',
        address: '',
        email: '',
        applyVat: true,
        comment: '',
        paymentMethod: 'cash',
        saveToDb: true
      }
    },
    recalculateFromBase(item) {
      console.log('item: ', item);
      
      const activeTaxRate = this.invoice.applyVat ? item.taxRate : 0;
      item.targetGross = Number(((item.basePrice - item.discount) * (1 + activeTaxRate)).toFixed(2));
    },
    localMoney(moneyToFormat) {
        return moneyFormat(moneyToFormat)
    },
    recalculateFromDiscount(item) {
      this.recalculateFromBase(item);
    },

    recalculateFromTargetGross(item) {
      if (!item.targetGross || item.targetGross <= 0) {
        item.discount = 0;
        return;
      }
      const activeTaxRate = this.invoice.applyVat ? item.taxRate : 0;
      const targetNetUnitPrice = item.targetGross / (1 + activeTaxRate);
      item.discount = Number((item.basePrice - targetNetUnitPrice).toFixed(4));
    },

    handleItemSelection(val) {
      if (!val) return;

      if (typeof val === 'object') {
        console.log('val objekt', val)
        this.documentDet = defDocumentDetail()
        this.documentDet = {...this.documentDet, ...defNewDocumentDetail()}
        const newId = this.getRndInteger(1000000000, 9000000000)

        this.documentDet.itemID = val.itemID
        this.documentDet.itemName = val.itemName
        this.documentDet.ItemNameShort = val.ItemNameShort
        this.documentDet.ItemCode = val.ItemCode
        this.documentDet.ItemEANCode = val.ItemEANCode
        this.documentDet.itemUnit = val.itemUnit
        if (val.GroupId) {
          this.documentDet.itemGroup = val.GroupId
        }
        if (val.itemGroup) {
          this.documentDet.itemGroup = val.itemGroup
        }

        this.documentDet.itemGroupName = val.GroupName ? val.GroupName : ''

        this.documentDet.itemSuppBasePrice = val.itemSuppBasePrice
        this.documentDet.itemSuppBasePriceAmount = val.itemSuppBasePrice
        this.documentDet.itemSuppPrice = val.itemSuppPrice
        this.documentDet.itemSuppPriceAmount = val.itemSuppPrice
        this.documentDet.ItemSuppCode = val.ItemSuppCode


        this.documentDet.itemPrice = val.itemPrice
        this.documentDet.itemVAT1Percentage = val.itemVAT1Percentage
        this.documentDet.itemVAT1Amount = val.itemPriceWithVAT - val.itemPrice
        this.documentDet.itemVAT2Percentge = val.itemVAT2Percentge
        this.documentDet.itemVAT2Amount = 0
        this.documentDet.itemDirectDuty1 = val.itemiDirectDuty1
        this.documentDet.itemDirectDuty2 = 0
        this.documentDet.itemPriceWithVAT = val.itemPriceWithVAT
        this.documentDet.itemPcsPlus = 0
        this.documentDet.itemPcsMinus = 1
        this.documentDet.itemPcsNeutral = 0
        this.documentDet.itemService = val.itemService

        this.documentDet.ItemHomeCurrency = 'EUR'
        this.documentDet.ItemForeignCurrency = 'EUR'
        this.documentDet.ItemExRate = 1

        this.documentDet.itemBaseAmount = val.itemPrice
        this.documentDet.itemBaseForeAmount = val.itemPrice
        this.documentDet.itemDiscountAmount1 = 0
        this.documentDet.itemDiscountForeAmount1 = 0
        this.documentDet.itemDiscountPercentage1 = 0
        this.documentDet.itemDiscountAmount2 = 0
        this.documentDet.itemDiscountPercentage2 = 0
        this.documentDet.itemDiscountAmount3 = 0
        this.documentDet.itemDiscountPercentage4 = 0
        this.documentDet.itemDiscountPrice = val.itemPrice
        this.documentDet.itemDiscountForePrice = val.itemPrice
 
        this.documentDet.itemBaseAmountNoVAT = val.itemPrice
        this.documentDet.itemBaseForeAmountNoVAT = val.itemPrice
        this.documentDet.itemBaseAmountWithVAT = val.itemPriceWithVAT
        this.documentDet.itemBaseForeAmountWithVAT = val.itemPriceWithVAT
        this.documentDet.itemAmountWithVAT = this.iAmountWithVAT
        this.documentDet.itemFullTotal = val.itemPriceWithVAT
        this.documentDet.itemFullTotalFore = val.itemPriceWithVAT

        this.documentDet.id = newId
        // documentItems is no longer accumulated here: it was built once per scan and
        // never followed the row edits, so what was saved differed from the screen.
        // It is now derived from cartItems at save time by cartToDetails().

        // The cart column is "Neto cijena" and the total adds the VAT on top of it,
        // so the gross price must not go in here - that charged the VAT twice.
        // The gross price is what the customer is quoted, so the net is derived from
        // it; itemPrice is only a fallback when the article has no gross price.
        const vatRate = round((val.itemVAT1Percentage / 100), 2)
        this.addCartItem(val.itemName, this.netFromGross(val, vatRate), vatRate, {
          itemService: val.itemService === true,
          itemUnit: val.itemUnit || '',
          itemID: val.itemID || null,
          ItemCode: val.ItemCode || null,
          ItemEANCode: val.ItemEANCode || null,
          itemGroup: val.GroupId || val.itemGroup || null,
          PotpunaSifra: val.PotpunaSifra || null
        });
      }
      // straight back to the search field so the next scan just works
      this.$nextTick(() => { this.itemSearchModel = null; this.focusSearch(); });
    },
    // A scanner types the code and sends Enter, so the list has to match the barcode
    // and the article code too - not only the name.
    itemFilter (item, queryText) {
      const q = String(queryText || '').toLowerCase().trim()
      if (!q) { return true }
      return ['itemName', 'ItemCode', 'ItemEANCode', 'itemID'].some(f =>
        item && item[f] && String(item[f]).toLowerCase().indexOf(q) !== -1)
    },

    // Exact code match wins - that is a scan. Otherwise fall back to the single
    // remaining match, and only then offer to create a free item.
    onSearchEnter () {
      const raw = this.itemSearchModel
      if (raw && typeof raw === 'object') { return }
      const q = String(raw || '').trim()
      if (!q) { return }
      const lower = q.toLowerCase()
      const exact = this.itemDatabase.find(i => i &&
        [i.ItemEANCode, i.ItemCode, i.itemID].some(c => c && String(c).toLowerCase() === lower))
      if (exact) {
        this.itemSearchModel = exact
        this.handleItemSelection(exact)
        return
      }
      const matches = this.itemDatabase.filter(i => this.itemFilter(i, q))
      if (matches.length === 1) {
        this.itemSearchModel = matches[0]
        this.handleItemSelection(matches[0])
        return
      }
      if (matches.length === 0) { this.addCustomItemDirectly() }
    },

    // database -> screen. Everything comes from posMapper so the same rules are
    // used here and (later) when saving back.
    loadFromDocument () {
      const details = this.$store.state.documentActivItems ? this.$store.state.documentActivItems : []
      console.log('details', details)
      this.cartItems = detailsToCart(details, this.document.documentSide)
      this.invoice = documentToInvoice(this.document, this.invoice)
      this.customerType = documentToCustomerType(this.document)

      // restore the payment that was chosen when the receipt was saved
      const pay = documentToPayment(this.document)
      this.invoice.paymentMethod = pay.paymentMethod || 'cash'
      this.cashTendered = pay.cashTendered

      // show the saved customer as selected, otherwise the box looks empty and it is
      // not clear whether a company or a private buyer is on the receipt
      const pid = this.document.partnerId ? parseInt(this.document.partnerId) : null
      const vat = (this.document.partnerVATID || '').trim()
      this.selectedPartner = this.allPartners.find(p =>
        (pid && parseInt(p.id) === pid) || (vat && p.partnerVATID === vat)) || null
      // The search box stays empty unless a real saved partner is behind the receipt.
      // A walk-in typed by hand is shown in the customer panel below, not here.

      if (this.invoice.vatId) { this.showPrivateVat = true }

      // Some older POS lines carry a discount only in the amount fields, never in the
      // price. Those cannot be rebuilt from the price, so re-saving would silently
      // change the invoice. Compare and refuse rather than quietly rewrite it.
      this.loadMismatch = null
      this.allowMismatchSave = false
      const stored = parseFloat(this.document.documentTotalAmountBrutto)
      if (!isNaN(stored) && stored > 0 && this.cartItems.length > 0) {
        const calculated = this.totals.finalTotal
        if (Math.abs(Math.round((calculated - stored) * 100)) > 2) {
          this.loadMismatch = {stored: stored, calculated: calculated}
        }
      }

      if (details.length === 0) {
        this.notify('Račun nema stavki ili stavke nisu učitane.', 'warning')
      }
    },

    // screen -> database. One place, so the saved lines are exactly the cart rows.
    // true when saving would change a loaded invoice that could not be rebuilt
    mismatchBlocks () {
      if (!this.loadMismatch || this.allowMismatchSave) { return false }
      this.notify('Račun se ne slaže s izračunom iz stavaka. Provjerite stavke ili potvrdite spremanje.', 'error')
      return true
    },

    // Nothing chosen means a walk-in customer. The receipt still needs a name.
    ensurePartnerName () {
      const name = (this.document.partnerName || '').trim()
      if (name.length === 0) {
        this.document.partnerName = 'POS'
        this.document.partnerNameShort = 'POS'
        this.document.partnerNameDatabase = 'POS'
        this.document.privatePerson = true
      } else {
        this.document.privatePerson = this.customerType === 'private'
      }
    },

    buildDocumentItems () {
      this.documentItems = cartToDetails(
        this.cartItems,
        this.document,
        () => ({...defDocumentDetail(), ...defNewDocumentDetail()})
      )
      invoiceToDocument(this.invoice, this.document, this.cashTendered, this.totals.finalTotal)
      return this.documentItems
    },

    clearCustomer () {
      this.selectedPartner = null
      this.clearAllPartnerData()
      this.invoice.email = ''
    },

    partnerAddress (p) {
      if (!p) { return '' }
      const street = [p.partnerStreet, p.partnerBuildingNameNumber].filter(x => x).join(' ').trim()
      const city = [p.partnerPostcode, p.partnerCity].filter(x => x).join(' ').trim()
      return [street, city].filter(x => x).join(', ')
    },

    // Same accent folding the rest of the app uses, so "miholic" finds "MIHOLIČ".
    foldText (text) {
      const from = 'ČĆŽŠĐčćžšđÁÀÂÄÃÅáàâäãåÉÈÊËéèêëÍÌÎÏíìîïÓÒÔÖÕóòôöõÚÙÛÜúùûüÝýÑñÇç'
      const to = 'CCZSDcczsdAAAAAAaaaaaaEEEEeeeeIIIIiiiiOOOOOoooooUUUUuuuuYyNnCc'
      return String(text).replace(/[\s\S]/g, (ch) => {
        const at = from.indexOf(ch)
        return at === -1 ? ch : to[at]
      }).toLowerCase()
    },

    // Every word has to be found, but each one may sit in a different field, so
    // "pana zagreb" matches the name in one field and the city in another.
    partnerFilter (item, queryText) {
      const q = String(queryText || '').trim()
      if (!q) { return true }
      const hay = this.foldText([
        item.partnerName, item.partnerVATID, item.partnerStreet,
        item.partnerBuildingNameNumber, item.partnerCity, item.partnerPostcode,
        item.partneremail
      ].filter(x => x).join(' '))
      return q.split(/\s+/).every(word => hay.indexOf(this.foldText(word)) !== -1)
    },

    // A private buyer typed at the till is not in Partners yet. Save the ones that
    // have enough data so the next time they can be found by the search instead of
    // being typed again. Nothing is saved when the customer was picked from the list.
    // Ponuda is printed from what is on screen - the receipt does not have to be
    // saved first, so a customer can be given an offer without creating a document.
    printPonuda () { this.printIspis('ponuda') },
    printRadniNalog () { this.printIspis('radninalog') },

    printIspis (vrsta) {
      if (!this.cartItems || this.cartItems.length === 0) {
        this.notify('Dodajte barem jednu stavku prije ispisa.', 'warning')
        return
      }
      this.ensurePartnerName()
      this.applyDefaultBank()

      const items = cartToDetails(this.cartItems, this.document, () => ({}))
      const doc = Object.assign({}, this.document, {
        documentTotalAmountNetto: round(this.totals.subtotal, 2),
        documentTotalAmountVAT: round(this.totals.totalTax, 2),
        documentTotalAmountBrutto: round(this.totals.finalTotal, 2),
        // napomena se na dokument upisuje tek kod spremanja, a ispis ide i prije toga
        documentDescription4: this.invoice.comment || this.document.documentDescription4 || ''
      })
      this.$refs.ponuda.print({
        vrsta: vrsta,
        doc: doc,
        items: items,
        comp: this.$store.state.companyActiv || {}
      })
    },

    // Fills the structure the local print service expects and sends it to
    // localhost:3005. Printing must never block the sale, so a printer that is not
    // running only produces a warning.
    async printReceipt () {
      const payload = buildPrintPayload({
        cartItems: this.cartItems,
        invoice: this.invoice,
        document: this.document,
        totals: this.totals,
        cashTendered: this.cashTendered,
        company: this.$store.state.companyActiv || {},
        user: this.$store.state.user || {},
        branch: this.branchForPrint()
      })
      const res = await sendToPrinter(payload)
      if (res.ok) {
        this.notify('Račun poslan na printer.', 'success')
      } else {
        this.notify('Printer servis nije dostupan (' + printServiceUrl() + '). Račun je spremljen.', 'warning')
        console.log('print service', res.status, res.body, payload)
      }
      return res
    },

    branchForPrint () {
      const id = this.$store.state.warehousemarkid
      const list = this.$store.state.warehouseState ? this.$store.state.warehouseState : []
      const found = list.find(w => w.WareHouseId === id)
      return found || {WareHouseId: id}
    },

    // Cash and card end up on the company account, so every receipt carries it.
    // The account marked DefaultAccount is the one that is used.
    async loadBankAccounts () {
      try {
        const query = {}
        query.CompanyId = this.$store.state.companyid ? this.$store.state.companyid : 0
        query.BusinessYear = this.$store.state.businessYear ? this.$store.state.businessYear : 0
        const res = await BankAccountService.choose(query)
        this.banks = res && res.data && res.data.bankAccounts ? res.data.bankAccounts : []
      } catch (err) {
        console.log('loadBankAccounts', err)
        this.banks = []
      }
      this.applyDefaultBank()
    },

    applyDefaultBank () {
      if (!this.banks || this.banks.length === 0) { return }
      // keep whatever a saved document already carries
      const savedId = this.document.bankAccountId ? parseInt(this.document.bankAccountId) : null
      const found = (savedId ? this.banks.find(b => parseInt(b.id) === savedId) : null) ||
        this.banks.find(b => b.DefaultAccount === true) ||
        this.banks[0]
      if (!found) { return }
      this.selectBank = found
      this.selectBankId = parseInt(found.id)
      this.document.bankAccountId = String(found.id)
      this.document.bankAccountName = found.BankName || ''
      this.document.bankAccountNr = found.IBAN || ''
    },

    async savePrivateBuyer () {
      if (this.customerType !== 'private') { return null }
      if (this.selectedPartner && typeof this.selectedPartner === 'object' && this.selectedPartner.id) { return null }

      const name = (this.document.partnerName || '').trim()
      if (name.length < 3) { return null }

      // do not create the same buyer twice
      const folded = this.foldText(name)
      const vat = (this.document.partnerVATID || '').trim()
      const already = this.allPartners.find(p =>
        (vat && p.partnerVATID === vat) || this.foldText(p.partnerName || '') === folded)
      if (already) {
        this.document.partnerId = already.id
        return already
      }

      const newPartner = {}
      newPartner.UserId = this.$store.state.user.id ? this.$store.state.user.id : ''
      newPartner.email = this.$store.state.user.email ? this.$store.state.user.email : 'test@cegrupa.com'
      newPartner.First = this.$store.state.user.First ? this.$store.state.user.First : 'NoFirst'
      newPartner.Last = this.$store.state.user.Last ? this.$store.state.user.Last : 'NoLast'
      newPartner.CompanyId = this.$store.state.companyid ? this.$store.state.companyid : 1
      newPartner.CompanyNameDatabase = this.$store.state.companyName ? this.$store.state.companyName : ''
      newPartner.BusinessYear = this.$store.state.businessYear ? this.$store.state.businessYear : 2026
      newPartner.partnerName = name
      newPartner.partnerNameShort = name.replace(/[\W_]/g, '')
      newPartner.partnerNameDatabase = name.replace(/[\W_]/g, '')
      newPartner.partnerStreet = this.document.partnerStreet || ''
      newPartner.partnerBuildingNameNumber = this.document.partnerBuildingNameNumber || ''
      newPartner.partnerCity = this.document.partnerCity || ''
      newPartner.partnerPostcode = this.document.partnerPostcode || ''
      // a private buyer is not required to give an OIB
      newPartner.partnerVATID = vat
      newPartner.partneremail = this.invoice.email || ''
      // this is what makes it a private buyer
      newPartner.privatePerson = true

      try {
        const res = await PartnerService.post(newPartner)
        const saved = res && res.data && res.data.partner ? res.data.partner : null
        if (saved && saved.id) {
          this.document.partnerId = saved.id
          // show up in the search straight away, without reloading the till
          const store = this.$store.state.partnerStore ? this.$store.state.partnerStore.slice() : []
          store.push(saved)
          this.$store.dispatch('setPartners', store)
          this.notify('Privatni kupac ' + name + ' je spremljen.', 'success')
        }
        return saved
      } catch (err) {
        // the receipt must not fail because the buyer could not be saved
        console.log('savePrivateBuyer', err)
        this.notify('Kupac nije spremljen, račun se svejedno izdaje.', 'warning')
        return null
      }
    },

    async onPartnerSelect (val) {
      if (!val) { this.clearAllPartnerData(); return }
      if (typeof val === 'string') {
        // typed, not picked - treat it as the name of a new customer
        this.document.partnerName = val
        return
      }
      // the saved record decides which kind of customer this is
      this.customerType = val.privatePerson ? 'private' : 'company'
      await this.findPartner(val.id)
      this.invoice.address = this.partnerAddress(val)
      this.invoice.email = val.partneremail || ''
      if (val.partnerVATID) { this.showPrivateVat = true }
      if (val.partneremail) {
        this.notify('Kupac ima email ' + val.partneremail + ' - račun se može poslati na email.', 'success')
      }
    },

    notify (message, color) {
      this.text = message
      this.color = color || 'success'
      this.timeout = color === 'error' ? 6000 : 4000
      this.snackbar = true
    },

    // Enter in a cart row means the line is finished - go back to scanning
    backToSearch () {
      this.itemSearchModel = null
      this.$nextTick(() => { this.focusSearch() })
    },

    focusSearch () {
      const c = this.$refs.itemSearch
      if (!c) { return }
      if (typeof c.focus === 'function') { c.focus() }
      const el = c.$el ? c.$el.querySelector('input') : null
      if (el) { el.focus(); el.select() }
    },

    // F2 back to search, F4 next payment method, F12 finish the receipt
    onGlobalKey (e) {
      if (e.key === 'F2') {
        e.preventDefault()
        this.itemSearchModel = null
        this.$nextTick(() => { this.focusSearch() })
      }
      if (e.key === 'F3') {
        e.preventDefault()
        this.invoice.paymentMethod = 'cash'
        this.$nextTick(() => {
          const f = this.$refs.tenderedField
          const el = f && f.$el ? f.$el.querySelector('input') : null
          if (el) { el.focus(); el.select() }
        })
      }
      if (e.key === 'F4') {
        e.preventDefault()
        const list = this.paymentMethods.map(m => m.value)
        const at = list.indexOf(this.invoice.paymentMethod)
        this.invoice.paymentMethod = list[(at + 1) % list.length]
      }
      if (e.key === 'F12') {
        e.preventDefault()
        if (this.cartItems.length > 0 && !this.isProcessingTerminal) {
          this.handleInvoiceSubmission()
        }
      }
    },

    addCustomItemDirectly() {
      if (!this.itemSearchModel) return;
      const cleanName = typeof this.itemSearchModel === 'string' ? this.itemSearchModel : this.itemSearchModel.name;
      this.addCartItem(cleanName, 0.00, 0.25);
      this.$nextTick(() => { this.itemSearchModel = null; this.focusSearch(); });
    },
    // Net unit price for the cart. Derived from the gross so the receipt total comes
    // out at the price the customer was quoted.
    netFromGross (item, taxRate) {
      const gross = parseFloat(item.itemPriceWithVAT)
      const net = parseFloat(item.itemPrice)
      const rate = parseFloat(taxRate) || 0
      // 4 decimals, not 2: rounding the net to cents makes the receipt total miss the
      // quoted gross price by a cent on ~700 of the articles. With 4 decimals the
      // total lands on the quoted price for every article in the base.
      if (!isNaN(gross) && gross > 0) {
        return Math.round((gross / (1 + rate)) * 10000) / 10000
      }
      return !isNaN(net) ? Math.round(net * 10000) / 10000 : 0
    },

    addCartItem(name, basePrice, taxRate, extra) {
      const existing = this.cartItems.find(i => i.name.toLowerCase() === name.toLowerCase() && i.taxRate === taxRate);
      if (existing) {
        existing.qty++;
        this.recalculateFromBase(existing);
      } else {
        let newItem = { name, qty: 1, basePrice, taxRate, discount: 0, targetGross: 0 };
        // itemService, itemUnit, itemID ... come from the article and travel with the row
        if (extra) { Object.assign(newItem, extra); }
        this.recalculateFromBase(newItem);
        this.cartItems.push(newItem);
      }
    },
    removeItem(idx) { 
      this.cartItems.splice(idx, 1); 
    },

    async onPrivateClientSelect(val) {
      if (val && typeof val === 'object') {
        await this.findPartner(val.id)
        this.invoice.email = val.email || '';
      } else if (typeof val === 'string') {
        this.document.partnerName = val;
      }
    },
    async onCompanySelect(val) {
      if (val && typeof val === 'object') {
        await this.findPartner(val.id)
        this.invoice.address = val. partnerStreet + ' ' + val.partnerBuildingNameNumber + ', ' + val.partnerPostcode + ' ' + val.partnerCity;
        this.invoice.email = val.partneremail || '';
      }
    },
    async findPartner(byId) {
        const locPartner =  (await PartnerService.findbyid(byId)).data.partner
        if (locPartner) {
          this.setPartner(locPartner)
        }
    },
    getRndInteger(min, max) {
      return Math.floor(Math.random() * (max - min) ) + min;
    },

    async verifyVatCompany() {
      //alert(`Tražim partnera po OIBu: ${this.document.partnerVATID}`);
      const checkPartner = {}
      checkPartner.CompanyId = this.$store.state.companyid
      checkPartner.partnerVATID = this.document.partnerVATID
      const {data} = await PartnerService.check(checkPartner)
      if (data && data.partner) {
        this.setPartner(data.partner)
      }
    },

    setPartner(locPartner) {
        this.document.partnerId = locPartner.id
        this.document.partnerName = locPartner.partnerName
        this.document.partnerNameShort = locPartner.partnerNameShort
        this.document.partnerNameDatabase = locPartner.partnerNameDatabase
        this.document.partnerBuildingNameNumber = locPartner.partnerBuildingNameNumber
        this.document.partnerStreet = locPartner.partnerStreet
        this.document.partnerCity = locPartner.partnerCity
        this.document.partnerPostcode = locPartner.partnerPostcode
        this.document.partnerCountyRegion = locPartner.partnerCountyRegion
        this.document.partnerState = locPartner.partnerState
        this.document.partnerVATID = locPartner.partnerVATID
        this.document.partneremail = locPartner.partneremail
        this.document.partnerPhones = locPartner.partnerPhones
        this.document.partnerBankName = locPartner.partnerBankName
        this.document.partnerBankAddress = locPartner.partnerBankAddress
        this.document.partnerBankIBAN = locPartner.partnerBankIBAN
        this.document.partnerBankSWIFT = locPartner.partnerBankSWIFT
        this.document.partnerContactTime = locPartner.partnerContactTime
        this.document.partnerSecretaryTitle = locPartner.partnerSecretaryTitle
        this.document.partnerSecretaryFirst = locPartner.partnerSecretaryFirst
        this.document.partnerSecretaryLast = locPartner.partnerSecretaryLast
        this.document.partnerSecretaryEmail = locPartner.partnerSecretaryEmail
        this.document.partnerSecretaryPhone = locPartner.partnerSecretaryPhone
        this.document.partnerSecretaryContactOption = locPartner.partnerSecretaryContactOption
        this.document.privatePerson = locPartner.privatePerson
    },

    async handleInvoiceSubmission() {
      if (this.customerType === 'company' && (!this.document.partnerName || !this.document.partnerVATID)) {
        this.notify('Molim ispunite potrebna polja za firmu.', 'error');
        return;
      }
      if (this.deliveryMethod === 'email' && !this.invoice.email) {
        this.notify('Za slanje računa na email treba upisati email adresu kupca.', 'error');
        return;
      }
      // primljeno is optional, but if it was entered it has to cover the bill
      if (this.invoice.paymentMethod === 'cash' &&
          this.cashTendered !== null && this.cashTendered !== '' &&
          this.cashChange < 0) {
        this.notify('Primljeni iznos je manji od ukupnog iznosa računa.', 'error');
        return;
      }

      if (this.mismatchBlocks()) { return }
      await this.savePrivateBuyer()
      this.ensurePartnerName()
      this.applyDefaultBank()
      this.buildDocumentItems()
      this.makeSum()

      if (this.isCardPayment) {
        this.executeTerminalHandshake();
      } else {
        await this.commitInvoiceFinalData();
      }
    },

    makeSum() {
      const updateSumObject = {
        documentDescription9: 0,
        documentDescription10: 0,
        documentTotalVAT1Per: 0,
        documentTotalVAT1Base: 0,
        documentTotalVAT1Amount: 0,
        documentTotalVAT2Per: 0,
        documentTotalVAT2Base: 0,
        documentTotalVAT2Amount: 0,
        documentTotalVAT3Per: 0,
        documentTotalVAT3Base: 0,
        documentTotalVAT3Amount: 0,
        documentTotalVAT4Per: 0,
        documentTotalVAT4Base: 0,
        documentTotalVAT4Amount: 0,
        documentTotalVAT5Per: 0,
        documentTotalVAT5Base: 0,
        documentTotalVAT5Amount: 0,
        documentTotalAmountNetto: 0,
        documentTotalAmountVAT: 0,
        documentTotalAmountBrutto: 0,
        documentTotalAmountPayInAdvance: this.$store.state.documentActivHead.documentTotalAmountPayInAdvance ? parseFloat(this.$store.state.documentActivHead.documentTotalAmountPayInAdvance) : 0,
        documentTotalAmountToPay: 0,
        documentTotalAmountNettoForeign: 0,
        documentTotalAmountVATForeign: 0,
        documentTotalAmountBruttoForeign: 0,
        documentTotalAmountPayInAdvanceForeign: 0,
        documentTotalAmountToPayForeign: 0,
        documentTotalAmountDirectD1: 0,
        documentTotalAmountDirectD2: 0,
        documentTotalAmountDirectD1Fore: 0,
        documentTotalAmountDirectD2Fore: 0,
        supplierAmount: 0,
        supplierNetoAmount: 0,
        supplierDiscount: 0,
        supplierMargin: 0,
        FreeF1: 0,
        FreeF2: 0,
        FreeF3: 0,
        FreeF4: 0,
        FreeF5: 0,
        FreeF6: 0,
      }
      let dSide = 2

      const hrVat1 = 25
      const hrVat2 = 13
      const hrVat3 = 5

      if (this.documentItems) {
        this.documentItems.map(documentDet => {
          this.pcsLoc = 0
          if (dSide === 1 ) {
            this.pcsLoc = parseFloat(documentDet.itemPcsPlus) ? parseFloat(documentDet.itemPcsPlus)  : 0
          }
          if (dSide === 2 ) {
            this.pcsLoc = parseFloat(documentDet.itemPcsMinus) ? parseFloat(documentDet.itemPcsMinus)  : 0
          }
          if (dSide === 3 ) {
            this.pcsLoc = parseFloat(documentDet.itemPcsNeutral) ? parseFloat(documentDet.itemPcsNeutral)  : 0
          }
          this.pcsLoc = round(this.pcsLoc, 3)     
          
          if ((documentDet.itemSuppBasePriceAmount)) {
            updateSumObject.supplierAmount += round((documentDet.itemSuppBasePriceAmount),2)
          }
          if (documentDet.itemSuppDiscountAmount) {
            updateSumObject.supplierDiscount += round(documentDet.itemSuppDiscountAmount,2)
          }
          if (documentDet.itemSuppPriceAmount) {
            updateSumObject.supplierNetoAmount += round(documentDet.itemSuppPriceAmount,2)
          }
          if (documentDet.itemBaseAmount) {
            updateSumObject.documentDescription9 += parseFloat(documentDet.itemBaseAmount)
          }
          if (documentDet.itemBaseAmountNoVAT) {
            updateSumObject.documentTotalAmountNetto += parseFloat(documentDet.itemBaseAmountNoVAT)
          }
          // if (documentDet.itemVAT1Amount) {
          //   updateSumObject.documentTotalAmountVAT += parseFloat(documentDet.itemVAT1Amount)
          // }
          //const dD1 = documentDet.itemDirectDuty1Amount ? parseFloat(documentDet.itemDirectDuty1Amount) : 0
          // if (documentDet.itemAmountWithVAT) {
          //   updateSumObject.documentTotalAmountBrutto += parseFloat(documentDet.itemAmountWithVAT) + dD1
          // }
          if (round(documentDet.itemVAT1Percentage, 2) === hrVat1) {
            updateSumObject.documentTotalVAT1Base += parseFloat(documentDet.itemBaseAmountNoVAT)
            // updateSumObject.documentTotalVAT1Amount += parseFloat(documentDet.itemVAT1Amount)
            // updateSumObject.documentTotalVAT1Base += parseFloat((documentDet.itemAmountWithVAT) - parseFloat(documentDet.itemVAT1Amount))
          }
          if (round(documentDet.itemVAT1Percentage, 2) === hrVat2) {
            updateSumObject.documentTotalVAT2Base += parseFloat(documentDet.itemBaseAmountNoVAT)
            // updateSumObject.documentTotalVAT2Amount += parseFloat(documentDet.itemVAT1Amount)
            // updateSumObject.documentTotalVAT2Base += parseFloat((documentDet.itemAmountWithVAT) - parseFloat(documentDet.itemVAT1Amount))
          }
          if (round(documentDet.itemVAT1Percentage, 2) === hrVat3) {
            updateSumObject.documentTotalVAT3Base += parseFloat(documentDet.itemBaseAmountNoVAT)
            // updateSumObject.documentTotalVAT3Amount += parseFloat(documentDet.itemVAT1Amount)
            // updateSumObject.documentTotalVAT3Base += (parseFloat(documentDet.itemAmountWithVAT) - parseFloat(documentDet.itemVAT1Amount))
          }
          if (documentDet.itemBaseAmount) {
            if (parseInt(documentDet.FreeF1) === 1) {
              updateSumObject.FreeF1 += parseFloat(documentDet.itemBaseAmount)
            }
            if (parseInt(documentDet.FreeF1) === 2) {
              updateSumObject.FreeF2 += parseFloat(documentDet.itemBaseAmount)
              updateSumObject.FreeF5 += parseFloat(documentDet.empWrkHrs)
            }
            if (parseInt(documentDet.FreeF1) === 3) {
              updateSumObject.FreeF3 += parseFloat(documentDet.itemBaseAmount)
            }
          }
          if (documentDet.itemBaseAmountNoVAT) {
            if (round(parseFloat(documentDet.itemVAT1Amount),2) === 0) {
              updateSumObject.FreeF4 += parseFloat(documentDet.itemBaseAmountNoVAT)
            }
          }
          if (documentDet.itemDirectDuty1Amount) {
            updateSumObject.documentTotalAmountDirectD1+= parseFloat(documentDet.itemDirectDuty1Amount)
          }
        })
      }

      updateSumObject.documentTotalAmountNetto = updateSumObject.documentTotalAmountNetto ? round(updateSumObject.documentTotalAmountNetto, 2) : 0
      updateSumObject.documentTotalVAT1Amount  = round((updateSumObject.documentTotalVAT1Base * ( (hrVat1/100))),2)
      updateSumObject.documentTotalVAT2Amount  = round((updateSumObject.documentTotalVAT2Base * ( (hrVat2/100))),2)
      updateSumObject.documentTotalVAT3Amount  = round((updateSumObject.documentTotalVAT3Base * ( (hrVat3/100))),2)
      updateSumObject.documentDescription9 = updateSumObject.documentDescription9 ? round(updateSumObject.documentDescription9, 2) : 0
      updateSumObject.supplierNetoAmount = updateSumObject.supplierNetoAmount ? round(updateSumObject.supplierNetoAmount, 2) : 0
      updateSumObject.supplierMargin = round(updateSumObject.documentTotalAmountNetto - updateSumObject.supplierNetoAmount, 2)
      updateSumObject.documentTotalAmountVAT = updateSumObject.documentTotalVAT1Amount + updateSumObject.documentTotalVAT2Amount + updateSumObject.documentTotalVAT3Amount
      updateSumObject.documentTotalAmountBrutto = updateSumObject.documentTotalAmountNetto + updateSumObject.documentTotalAmountVAT
      updateSumObject.documentTotalVAT1Per = hrVat1 ? round(hrVat1, 2) : 0
      updateSumObject.documentTotalVAT2Per = hrVat2 ? round(hrVat2, 2) : 0
      updateSumObject.documentTotalVAT3Per = hrVat3 ? round(hrVat3, 2) : 0

      // updateSumObject.documentTotalVAT1Base = hrVat1 && updateSumObject.documentTotalVAT1Amount ? round(updateSumObject.documentTotalVAT1Amount / (hrVat1/100), 2) : 0
      // updateSumObject.documentTotalVAT2Base = hrVat2 && updateSumObject.documentTotalVAT2Amount ? round(updateSumObject.documentTotalVAT2Amount / (hrVat2/100), 2) : 0
      // updateSumObject.documentTotalVAT3Base = hrVat3 && updateSumObject.documentTotalVAT3Amount ? round(updateSumObject.documentTotalVAT3Amount / (hrVat3/100), 2) : 0
      updateSumObject.FreeF1 = updateSumObject.FreeF1 ? round(updateSumObject.FreeF1, 2) : 0
      updateSumObject.FreeF2 = updateSumObject.FreeF2 ? round(updateSumObject.FreeF2, 2) : 0
      updateSumObject.FreeF3 = updateSumObject.FreeF3 ? round(updateSumObject.FreeF3, 2) : 0
      updateSumObject.FreeF4 = updateSumObject.FreeF4 ? round(updateSumObject.FreeF4, 2) : 0
      updateSumObject.FreeF5 = updateSumObject.FreeF5 ? round(updateSumObject.FreeF5, 2) : 0
      updateSumObject.documentDescription10 = updateSumObject.documentDescription9 - updateSumObject.documentTotalAmountNetto
      updateSumObject.documentTotalAmountPayInAdvance = updateSumObject.documentTotalAmountPayInAdvance ? round(updateSumObject.documentTotalAmountPayInAdvance, 2) : 0
      updateSumObject.documentTotalAmountToPay = updateSumObject.documentTotalAmountBrutto ? (round(updateSumObject.documentTotalAmountBrutto, 2) - (updateSumObject.documentTotalAmountPayInAdvance )): 0

      updateSumObject.documentTotalAmountNettoForeign = updateSumObject.documentTotalAmountNetto ? round((updateSumObject.documentTotalAmountNetto / this.ExRate), 2) : 0
      updateSumObject.documentTotalAmountVATForeign = updateSumObject.documentTotalAmountVAT ? round((updateSumObject.documentTotalAmountVAT / this.ExRate), 2) : 0
      updateSumObject.documentTotalAmountBruttoForeign = updateSumObject.documentTotalAmountBrutto ? round((updateSumObject.documentTotalAmountBrutto / this.ExRate), 2) : 0
      updateSumObject.documentTotalAmountPayInAdvanceForeign = round((updateSumObject.documentTotalAmountPayInAdvance  / this.ExRate), 2)
      updateSumObject.documentTotalAmountToPayForeign = updateSumObject.documentTotalAmountBrutto ? (round((updateSumObject.documentTotalAmountBrutto/ this.ExRate), 2) - (updateSumObject.documentTotalAmountPayInAdvance  / this.ExRate) ) : 0
      updateSumObject.documentTotalAmountDirectD1Fore = updateSumObject.documentTotalAmountDirectD1 ? round((updateSumObject.documentTotalAmountDirectD1 / this.ExRate), 2) : 0

      // The totals have to end up on the document that is actually saved. Before this
      // they were only put into the store, so every saved receipt kept zero sums.
      Object.assign(this.document, updateSumObject)

      const newHead = {...this.$store.state.documentActivHead, ...updateSumObject}
      if (newHead) {
          this.$store.dispatch('setDocumentActivHead', newHead)
        } else {
          this.$store.dispatch('setDocumentActivHead', {})
        }
    },

    async executeTerminalHandshake() {
      try {
        this.isProcessingTerminal = true;

        const sendQuery = {}
        sendQuery.CompanyId = this.$store.state.companyid ? this.$store.state.companyid : 1
        sendQuery.CompanyNameDatabase = this.$store.state.companyName ? this.$store.state.companyName : ''
        sendQuery.BusinessYear = this.$store.state.businessYear ? this.$store.state.businessYear : 2020
        sendQuery.documentName = 'POS'
        sendQuery.amount = 0.01
        sendQuery.invoiceId = this.getRndInteger(100000, 900000)
        sendQuery.posAppId = '1'
        sendQuery.posAppName = 'CEgrupa'
        sendQuery.DatumIzvrsenja = new Date()

        const res = TeyaService.initiate(sendQuery)
        console.log('TeyaService res: ', res);
        
        this.terminalTimeoutId = setTimeout(async () => {
          this.isProcessingTerminal = false;
          this.notify('Terminal je potvrdio plaćanje. Račun se može zaključiti.', 'success');
          await this.commitInvoiceFinalData();
        }, 4500);
      } catch (err) {
        console.log('Teya initiat err: ', err);
        }
    },

    abortTerminalPayment() {
      clearTimeout(this.terminalTimeoutId);
      this.isProcessingTerminal = false;
      this.notify('Transakcija je prekinuta. Novac nije skinut.', 'warning');
    },

    async commitInvoiceFinalData() {
      const payload = {
        meta: { customerType: this.customerType, deliveryMethod: this.deliveryMethod, paymentType: this.invoice.paymentMethod },
        billing: { ...this.invoice },
        cart: this.cartItems,
        accounting: { ...this.totals }
      };
      console.log('1  Glava ', this.document);
      console.log('2. detalji ', this.documentItems);
      console.log('Podaci u bazu uspješno upisani! ', payload);
      if (this.deliveryMethod === 'print') {
        await this.printReceipt();
      } else {
        this.notify('Račun je poslan na email.', 'success');
      }
      this.cartItems = [];
      this.cashTendered = null;
      this.$nextTick(() => { this.focusSearch(); });
    },
    clearPartner() {
      this.selectPa = null
      this.document.partnerId = null
      this.document.partnerName = null
      this.document.partnerNameShort = null
      this.document.partnerNameDatabase = null
      this.document.partnerBuildingNameNumber = null
      this.document.partnerStreet = null
      this.document.partnerCity = null
      this.document.partnerPostcode = null
      this.document.partnerCountyRegion = null
      this.document.partnerState = null
      this.document.partnerVATID = null
      this.document.partneremail = null
      this.document.partnerPhones = null
      this.document.partnerBankName = null
      this.document.partnerBankAddress = null
      this.document.partnerBankIBAN = null
      this.document.partnerBankSWIFT = null
      this.document.partnerContactTime = null
      this.document.partnerSecretaryTitle = null
      this.document.partnerSecretaryFirst = null
      this.document.partnerSecretaryLast = null
      this.document.partnerSecretaryEmail = null
      this.document.partnerSecretaryPhone = null
      this.document.partnerSecretaryContactOption = null
      this.document.privatePerson = false
    },

    async findPartner (findQuery) {
      //console.log('trazim  FIND PARTNER findQuery', findQuery, 'this.findpartnerbyid  ', this.findpartnerbyid )
      //if (partnerVATID.length > 0) {
        //const locPartner = !this.findpartnerbyid ? (await PartnerService.show(findQuery)).data.partner : (await PartnerService.findbyid(findQuery)).data.partner
        const locPartner =  (await PartnerService.findbyid(findQuery)).data.partner
        this.document.partnerId = locPartner.id
        this.document.partnerName = locPartner.partnerName
        this.document.partnerNameShort = locPartner.partnerNameShort
        this.document.partnerNameDatabase = locPartner.partnerNameDatabase
        this.document.partnerBuildingNameNumber = locPartner.partnerBuildingNameNumber
        this.document.partnerStreet = locPartner.partnerStreet
        this.document.partnerCity = locPartner.partnerCity
        this.document.partnerPostcode = locPartner.partnerPostcode
        this.document.partnerCountyRegion = locPartner.partnerCountyRegion
        this.document.partnerState = locPartner.partnerState
        this.document.partnerVATID = locPartner.partnerVATID
        this.document.partneremail = locPartner.partneremail
        this.document.partnerPhones = locPartner.partnerPhones
        this.document.partnerBankName = locPartner.partnerBankName
        this.document.partnerBankAddress = locPartner.partnerBankAddress
        this.document.partnerBankIBAN = locPartner.partnerBankIBAN
        this.document.partnerBankSWIFT = locPartner.partnerBankSWIFT
        this.document.partnerContactTime = locPartner.partnerContactTime
        this.document.partnerSecretaryTitle = locPartner.partnerSecretaryTitle
        this.document.partnerSecretaryFirst = locPartner.partnerSecretaryFirst
        this.document.partnerSecretaryLast = locPartner.partnerSecretaryLast
        this.document.partnerSecretaryEmail = locPartner.partnerSecretaryEmail
        this.document.partnerSecretaryPhone = locPartner.partnerSecretaryPhone
        this.document.partnerSecretaryContactOption = locPartner.partnerSecretaryContactOption
        this.document.privatePerson = locPartner.privatePerson

    //    this.selectPa = !this.findpartnerbyid ? this.selectPa = this.partners.find(partner => (partner.partnerVATID === (this.document.partnerVATID))) : this.selectPa = this.partners.find(partner => (partner.id === parseInt(this.document.partnerId)))
        this.$store.dispatch('setPartnerid', locPartner.id)
        //   }
        // }
     // }
    },

    async register () {
      try {
        // write new document to database
        // this.localError = false   
        console.log('Snimam 0', this.$store.state.businessYear)
        const haveYear = dayjs(this.document.documentDate).year()
        if (haveYear !== this.$store.state.businessYear) {
          this.text = this.lang.wrongBussinesYear
          this.color = 'red'
          this.snackbar = true
          this.timeout = 3000
          return
        }
        console.log('Snimam 1')
        if (this.mismatchBlocks()) { return }
        await this.savePrivateBuyer()
        this.ensurePartnerName()
        this.applyDefaultBank()
        this.buildDocumentItems()
        this.makeSum()

        // if (!this.document.exportDoc) {
        //   const partVatLeght = this.document.partnerVATID.trim().length
        //   if (partVatLeght !== 11) {
        //     this.text = this.lang.PartnerErrorVAT3
        //     this.color = 'red'
        //     this.snackbar = true
        //     this.timeout = 3000
        //     this.reqVATID = true
        //     return
        //   }
        // }
             this.document.documentExRate = 1
         console.log('SAVING this.$store.state.editItem: ', this.$store.state.editItem);
            // create NEW
            if (this.$store.state.editItem === 0) {
             
              this.document.documentId = null

              if (!this.document.partnerNameShort || (this.document.partnerNameShort.trim()).length < 1) {
                this.document.partnerNameShort = this.document.partnerName.replace(/[\W_]/g, '')
              }
              if (!this.document.partnerNameDatabase || (this.document.partnerNameDatabase.trim()).length < 1) {
                this.document.partnerNameDatabase = this.document.partnerName.replace(/[\W_]/g, '')
              }
              this.document.status = 'Nefiskaliziran'
              const lastNr = {}
              const side = 2
              lastNr.CompanyId = this.$store.state.companyid ? this.$store.state.companyid : 0
              lastNr.BusinessYear = this.$store.state.businessYear ? this.$store.state.businessYear : 2024
              if (side === 1 || side === 2) {
                lastNr.WareHouseId = this.$store.state.warehousemarkid ? this.$store.state.warehousemarkid : 1
              }
              lastNr.documentName = this.documentName

              this.document.CompanyNameDatabase = this.$store.state.companyName ? this.$store.state.companyName : ''
              this.document.BusinessYear = this.$store.state.businessYear ? this.$store.state.businessYear : 2024
             
              console.log('lastNr on doc create', lastNr)
              this.document.documentId = parseInt((await DocumentService.choose(lastNr)).data.docNumber)
              if (this.document.documentId && this.document.documentId > 0)
              {
                this.document.documentIdGeneral = this.$store.state.companyName + '-' + this.$store.state.businessYear + '-' + this.$store.state.documentActiv + '-' + this.document.documentId + '-' + (this.$store.state.warehousemarkid ? this.$store.state.warehousemarkid : 1)
                this.document.WareHouseId = (this.$store.state.warehousemarkid ? this.$store.state.warehousemarkid : 1)
                const whObj =  this.$store.state.warehouseState ? this.$store.state.warehouseState.find(whID => whID.WareHouseId === this.$store.state.warehousemarkid) : []
                this.document.WareHouseName = whObj && whObj.WareHouseName ? whObj.WareHouseName : ''
                await DocumentService.post(this.document)
                  .then(async(response) => {
                    const saved = response && response.data ? response.data : null
                    if (saved && saved.id) {
                      this.document.id = saved.id
                      this.$store.dispatch('setDatabaseDocID', saved.id)
                      this.stampDetails(saved)
                    }
                    await this.saveItems()
                    this.text = this.lang.DocumentSavedOK + response.statusText
                    this.color = 'green'
                    this.snackbar = true
                    this.timeout = 2000
                    this.$store.dispatch('seteditItem', 0)
                    this.$store.dispatch('setDocumentActivHead', {})
                    this.$store.dispatch('setDocumentActivItems', [])
                    const newDoc = response.data && response.data.newDoc ? response.data.newDoc : null
                    if (newDoc) {
                      this.documentlist()
                    }
                  })
                  .catch(err => {
                    // eslint-disable-next-line
                    console.log(err)
                    this.text = 'Error post!' + err
                    this.color = 'red'
                    this.snackbar = true
                    this.timeout = 4000
                  })
              }
              
            }
            // edit current
            if (this.$store.state.editItem === 1) {
              await DocumentService.put(this.document)
                .then(async(response) => {
                  this.stampDetails(this.document)
                  await this.saveItems()
                  this.text = this.lang.DocumentSavedOK + response.statusText
                  this.color = 'green'
                  this.snackbar = true
                  this.timeout = 2000
                  this.$store.dispatch('seteditItem', 0)
                  this.$store.dispatch('setDocumentActivHead', {})
                  this.$store.dispatch('setDocumentActivItems', [])
                  const newDoc = response.data && response.data.newDoc ? response.data.newDoc : null
                  if (newDoc) {
                    this.documentlist()
                  } else {
                    this.documentlist()
                  }
                })
                .catch(err => {
                  // eslint-disable-next-line
                  console.log(err)
                  this.text = 'Error put!' + err
                  this.color = 'red'
                  this.snackbar = true
                  this.timeout = 4000
                })
            }
      
      } catch (error) {
        // eslint-disable-next-line
        console.log(error)
        this.text = 'Error on save!' + error
        this.color = 'red'
        this.snackbar = true
        this.timeout = 4000
        this.error = 'Error'
      }
    },

    // saveitems() on the server deletes by databaseDocID and only then inserts, and it
    // does nothing at all when that id is missing. POSfast never set it, so the lines
    // were silently not saved. Stamp the saved document onto every line first.
    // After saving go back to the POS list. Called from every save path - it was
    // referenced before but never defined, so a successful save threw here.
    documentlist () {
      this.cartItems = []
      this.cashTendered = null
      this.documentItems = []
      this.$store.dispatch('seteditItem', 0)
      this.$store.dispatch('setDocumentActivHead', {})
      this.$store.dispatch('setDocumentActivItems', [])
      this.$router.push({ name: 'documentposindex' })
    },

    stampDetails (doc) {
      if (!doc || !doc.id) { return }
      this.documentItems.forEach(item => {
        item.databaseDocID = doc.id
        item.documentId = parseInt(doc.documentId)
        item.documentIdGeneral = doc.documentIdGeneral
        // the database assigns the row id - reusing an old one breaks the insert
        delete item.id
      })
    },

    async saveItems() {
          if (this.documentItems && this.documentItems.length > 0) {
            await DocumentDetailService.saveitems(this.documentItems).then(res => {
              this.text = this.langC.DocumentSaved + (res && res.data && res.data.message ? res.data.message : '')
              this.color = 'green'
              this.snackbar = true
            }).catch(err => {
              this.text = this.langC.DocumentSaveError + (err.data)
              this.color = 'red'
              this.snackbar = true
            })
          }
    }
  },
  components: {
    DocumentPonudaNalogPrint,
    POStestlist,
    POStestlist2
  }
};
</script>

<style scoped>
.border-all { border: 2px solid rgba(0, 0, 0, 0.15) !important; }
.border-top { border-top: 2px solid rgba(0, 0, 0, 0.15) !important; }
.cursor-pointer { cursor: pointer; }
.line-height-large { line-height: 1.6; }

@media (min-width: 960px) {
  .border-left-desktop { border-left: 2px solid rgba(0, 0, 0, 0.15) !important; }
}

/* Accessible Forms: Increase font sizing for text fields, dropdown selections and input indicators */
.accessible-field >>> .v-label { font-size: 1.15rem !important; }
.accessible-field >>> input { font-size: 1.15rem !important; padding: 12px 0 !important; }
.accessible-field >>> .v-input__slot { min-height: 56px !important; }

/* Scale up custom checkboxes and switch text targets */
.v-input--accessible-checkbox >>> .v-label { font-size: 1.2rem !important; font-weight: 500; color: #424242 !important; }
.v-input--accessible-checkbox >>> .v-icon { font-size: 28px !important; }
.v-input--accessible-switch >>> .v-label { font-size: 1.2rem !important; font-weight: bold; color: #2e7d32 !important; }

/* Accessible Datatable Configurations */
.accessible-table th { height: 60px !important; font-size: 1.1rem !important; }
.table-row-large td { height: 70px !important; border-bottom: 1px solid rgba(0,0,0,0.12) !important; }

/* In-table raw spreadsheet configuration adjustments */
.plain-text-field >>> .v-input__slot:before, .plain-text-field >>> .v-input__slot:after { display:none !important; }
.plain-text-field >>> .v-input__slot { padding: 0 !important; }
.center-input >>> input { text-align: center; font-size: 1.15rem !important; }
.center-input >>> .v-select__selection { width: 100%; text-align: center; justify-content: center; font-size: 1.15rem !important; }
.right-input >>> input { text-align: right; font-size: 1.15rem !important; font-weight: 500; }
.generic-gross-field >>> input { color: #1565C0 !important; font-weight: bold; }
</style>